import { Component, Inject, OnInit, ViewChild } from '@angular/core';
import { MatSelect } from '@angular/material/select';
import { MatDialogRef, MAT_DIALOG_DATA } from '@angular/material/dialog';
import { FormGroup, FormBuilder, Validators, AbstractControl } from '@angular/forms';

import { AhsTranslationService } from '@nx-ahs/base-translate';
import { BehaviorSubject } from 'rxjs';
import { SecureMessagingService } from '../../services/secure-messaging.service';
import { UtilitiesService } from '@nx-ahs/shared/services';
import { PhonePipe } from '@nx-ahs/shared/pipes';

import { ContactAccountModel } from '../../models/contact-account.model';
import { ContactTimeModel } from '../../models/contact-time.model';
import { ContactUsModel } from '../../models/contact-us.model';
import { ConversationCategoryModel } from '../../models/conversation-category.model';
import { ConversationPropertyModel } from '../../models/conversation-property.model';
import { ConversationResponseTypeModel } from '../../models/conversation-response-type.model';

@Component({
	selector: 'app-create-new-message',
	templateUrl: './create-new-message.component.html',
	styleUrls: ['./create-new-message.component.scss'],
	standalone: false
})
export class CreateNewMessageComponent implements OnInit {
	@ViewChild('subjectselect') subjectselect: MatSelect;
	@ViewChild('regardingselect') regardingselect: MatSelect;
	@ViewChild('timeselect') timeselect: MatSelect;

	public accounts: ContactAccountModel[];
	public contactTimes: ContactTimeModel[];
	public convResponseTypes: ConversationResponseTypeModel[];
	public disableSubmit: boolean = false;
	public message: string = '';
	public newMessageForm: FormGroup;
	public selectedContactChoice: string;
	public showErrorMessages: boolean = false;
	public subjects: ConversationCategoryModel[];

	public linkedEntityNameId: number = 3;

	constructor(
		public translationLoader: AhsTranslationService,
		public dialogRef: MatDialogRef<CreateNewMessageComponent>,
		private formBuilder: FormBuilder,
		private secureMessagingService: SecureMessagingService,
		private utilitySvc: UtilitiesService,
		private phonePipe: PhonePipe,
		@Inject('clientConfig') private clientConfig: BehaviorSubject<any>,
		@Inject(MAT_DIALOG_DATA) data
	) {
		this.accounts = data.data.accounts;
		this.subjects = data.data.subjects;
		this.convResponseTypes = data.data.convResponseTypes;
		this.contactTimes = data.data.contactTimes;
	}

	ngOnInit() {
		this.linkedEntityNameId = this.clientConfig?.value?.secureMessaging?.defaultLinkedEntityNameId || 3;
		this.buildForm();
	}

	private buildForm() {
		this.newMessageForm = this.formBuilder.group({
			regarding: [null, [Validators.required]],
			message: [null, [Validators.required]],
			contactChoice: [null, [Validators.required]],
			subject: [null, [Validators.required]],
			phoneNumber: [null, [Validators.required, Validators.minLength(10), Validators.maxLength(10)]],
			contactTime: [this.contactTimes[0].contactTimeId]
		}); //, { validator: validatePhoneNumber });
	}

	public createMessage() {
		if (this.isFormInvalid()) {
			this.showErrorMessages = true;
		} else {
			this.showErrorMessages = false;
			this.disableSubmit = true;

			const model = new ContactUsModel();
			const selectedPerson: ContactAccountModel = this.newMessageForm.get('regarding').value;
			model.entityId = selectedPerson.accountId;
			model.entityNameId = 28; // TODO: magic number
			model.message = this.newMessageForm.get('message').value;
			model.conversationResponseTypeId = this.newMessageForm.get('contactChoice').value;
			model.conversationCategoryId = this.newMessageForm.get('subject').value;

			const selectedSubject: ConversationCategoryModel = this.subjects.filter(
				(f) => f.conversationCategoryId === model.conversationCategoryId
			)[0];
			model.conversationTypeId = selectedSubject.conversationTypeId;

			const msgPropertyArray: ConversationPropertyModel[] = [];
			const regardingProperty = new ConversationPropertyModel();
			regardingProperty.propertyName = 'REGARDING';
			regardingProperty.propertyValue = selectedPerson.fullname;
			msgPropertyArray.push(regardingProperty);

			if (selectedPerson.accountId) {
				const regardingProperty = new ConversationPropertyModel();
				regardingProperty.propertyName = 'ACCOUNTID';
				regardingProperty.propertyValue = selectedPerson.accountId.toString();
				msgPropertyArray.push(regardingProperty);
			}

			const contactMethodProperty = new ConversationPropertyModel();
			contactMethodProperty.propertyName = 'PREFERRED CONTACT';
			contactMethodProperty.propertyValue = this.convResponseTypes.find(
				(x) => x.conversationResponseTypeId == model.conversationResponseTypeId
			).description;
			msgPropertyArray.push(contactMethodProperty);

			model.linkedEntityId = selectedPerson.personId;
			model.linkedEntityNameId = this.linkedEntityNameId;

			if (model.conversationResponseTypeId === 2) {
				// TODO: magic number
				model.phoneNumber = this.newMessageForm.get('phoneNumber').value;
				model.contactTimeId = this.newMessageForm.get('contactTime').value;

				const phoneProperty = new ConversationPropertyModel();
				phoneProperty.propertyName = 'PHONE NUMBER';
				phoneProperty.propertyValue = this.phonePipe.transform(model.phoneNumber);
				msgPropertyArray.push(phoneProperty);

				const contactProperty = new ConversationPropertyModel();
				contactProperty.propertyName = 'CONTACT TIME';
				contactProperty.propertyValue = this.contactTimes.find(
					(x) => x.contactTimeId === model.contactTimeId
				).description;
				msgPropertyArray.push(contactProperty);
			}

			model.messageProperties = JSON.stringify(msgPropertyArray);

			this.secureMessagingService.saveMessage(model).subscribe(
				(result) => {
					this.dialogRef.close(true);
				},
				(error) => {
					throw new Error(error);
				},
				() => {
					this.disableSubmit = false;
				}
			);
		}
	}

	public gainedFocus() {
		if (document.activeElement === this.subjectselect._elementRef.nativeElement && this.subjectselect.empty) {
			this.subjectselect.open();
		}
		if (document.activeElement === this.regardingselect._elementRef.nativeElement && this.regardingselect.empty) {
			this.regardingselect.open();
		}
		if (document.activeElement === this.timeselect?._elementRef.nativeElement && this.timeselect.empty) {
			this.timeselect.open();
		}
	}

	public showPhone() {
		const contactTimeControl: AbstractControl = this.newMessageForm.get('contactTime');

		if (this.newMessageForm.controls['contactChoice'].value === 2) {
			this.newMessageForm.controls['phoneNumber'].enable();
			contactTimeControl.setValidators([Validators.required]);
		} else {
			this.newMessageForm.controls['phoneNumber'].disable();
			contactTimeControl.clearValidators();
		}
	}

	private isFormInvalid() {
		if (this.newMessageForm.invalid) {
			this.utilitySvc.markAllFieldsTouched(this.newMessageForm);
			return true;
		} else return false;
	}
}
